import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { card, chip, ib, table } from "../../ui/record";
import { field, refuse, select } from "../clients/forms";

// The caregiver's pay package (brief 1E-1; renamed in brief 1E-30, DECISIONS 11/01): their agreed
// pay rate for each client, their default agreed pay rate and their title's pay rate card,
// each dated (a new rate starts on a date and ends the one before); and who gets paid what for a
// client, the chain with the one that wins. Only with the Money switch (the card isn't drawn
// otherwise); adding a rate needs the Edit pay rates switch. Brief 1E-4: Overtime (the company's
// rule, or exempt, such as a salaried RN), set with the same switch.

interface Rate {
  id: string;
  level: string;
  levelLabel: string;
  client: string | null;
  service: string;
  words: string;
  dates: string;
  live: boolean;
}
interface Pkg {
  person: { id: string; name: string; title: string | null };
  /** Brief 1E-4: overtime exempt, and the company's overtime rule. */
  overtime: {
    exempt: boolean;
    rule: string;
    /** Brief 1E-5: their own threshold (null: the company's), and their payroll employee ID. */
    afterHours: number | null;
    companyAfterHours: number;
    employeeId: string | null;
  };
  canEdit: boolean;
  today: string;
  rates: Rate[];
  roleDefault: Rate[];
  chains: {
    clientId: string;
    client: string;
    service: string | null;
    steps: { n: number; title: string; line: string; rate: string | null; state: string }[];
  }[];
  clients: { id: string; name: string }[];
  services: { id: string; name: string }[];
}

export async function payPackageCard(host: HTMLElement, personId: string) {
  let chainFor: string | null = null;

  async function load() {
    const res = await api<Pkg>("GET", `/api/people/${encodeURIComponent(personId)}/pay`);
    // Without the Money switch there's no card at all.
    if (!res.ok) return host.replaceChildren();
    draw(res.body);
  }

  function draw(p: Pkg) {
    const rows = [...p.rates, ...p.roleDefault.map((r) => ({ ...r, client: null }))];
    const pkg = card(
      "Pay package",
      "Agreed pay rate for the client, else the default, else the title's pay rate card.",
      p.canEdit ? [ib("plus", "Add an agreed pay rate", () => addModal(p))] : [],
      rows.length
        ? table(
            [
              {
                h: "Kind",
                f: (r: Rate) =>
                  el(
                    "span",
                    {},
                    el("b", {}, r.levelLabel),
                    r.client ? el("div", { class: "small muted" }, r.client) : null,
                  ),
              },
              { h: "Service", f: (r: Rate) => r.service },
              { h: "Pay rate", num: true, f: (r: Rate) => el("b", {}, r.words) },
              {
                h: "Dates",
                f: (r: Rate) => el("span", {}, r.dates, " ", r.live ? chip("Now", "ok") : ""),
              },
            ],
            rows,
            undefined,
            "Pay package",
          )
        : el(
            "p",
            { class: "muted" },
            "No agreed pay rates yet. Add one, or set the title's pay rate card in Payroll › Pay rates.",
          ),
    );
    // Overtime: the company's rule, or exempt (never paid overtime).
    const exempt = el("input", {
      type: "checkbox",
      checked: p.overtime.exempt,
      disabled: !p.canEdit,
    });
    exempt.addEventListener("change", async () => {
      const r = await api("PUT", `/api/people/${encodeURIComponent(personId)}/overtime`, {
        exempt: exempt.checked,
      });
      if (!r.ok) {
        exempt.checked = p.overtime.exempt;
        return toast("That wasn't saved", (r.body as { error?: string }).error);
      }
      p.overtime.exempt = exempt.checked;
      toast("Saved", exempt.checked ? "Exempt from overtime" : p.overtime.rule);
    });
    // Brief 1E-5: their own overtime threshold (empty: the company's) and their employee ID for
    // the payroll file; saved when changed.
    const save = async (b: Record<string, unknown>, said: string) => {
      const r = await api<{ afterHours: number | null; employeeId: string | null }>(
        "PUT",
        `/api/people/${encodeURIComponent(personId)}/overtime`,
        b,
      );
      if (!r.ok) return toast("That wasn't saved", r.body.error);
      p.overtime.afterHours = r.body.afterHours;
      p.overtime.employeeId = r.body.employeeId;
      toast("Saved", said);
    };
    const after = el("input", {
      type: "number",
      inputmode: "decimal",
      value: p.overtime.afterHours === null ? "" : String(p.overtime.afterHours),
      placeholder: String(p.overtime.companyAfterHours),
      disabled: !p.canEdit,
      "aria-label": "Overtime starts after (hours in the week)",
      style: "max-width:8em",
    });
    after.addEventListener(
      "change",
      () =>
        void save(
          { afterHours: after.value },
          after.value
            ? `Overtime after ${after.value} hours for them`
            : `The company's ${p.overtime.companyAfterHours} hours`,
        ),
    );
    const empId = el("input", {
      type: "text",
      value: p.overtime.employeeId ?? "",
      disabled: !p.canEdit,
      maxlength: "40",
      "aria-label": "Employee ID",
      style: "max-width:14em",
    });
    empId.addEventListener(
      "change",
      () => void save({ employeeId: empId.value }, "Employee ID for the payroll file"),
    );
    pkg.append(
      el(
        "dl",
        { class: "kv", style: "margin-top:10px" },
        el("dt", {}, "Overtime"),
        el(
          "dd",
          {},
          el("span", {}, p.overtime.exempt ? "Exempt" : p.overtime.rule),
          el("label", { class: "ptask" }, exempt, " Overtime exempt (for example a salaried RN)"),
        ),
        el("dt", {}, "Overtime starts after"),
        el(
          "dd",
          {},
          after,
          el(
            "div",
            { class: "small muted" },
            `Hours a week. Empty: the company's ${p.overtime.companyAfterHours}.`,
          ),
        ),
        el("dt", {}, "Employee ID"),
        el(
          "dd",
          {},
          empId,
          el("div", { class: "small muted" }, "The company's ID for them, on the payroll file"),
        ),
      ),
    );
    const parts: Node[] = [pkg];
    const chain = p.chains.find((c) => c.clientId === chainFor) ?? p.chains[0];
    if (chain) {
      const pick =
        p.chains.length > 1
          ? select(
              "For",
              p.chains.map((c) => [c.clientId, c.client] as const),
              chain.clientId,
            )
          : null;
      pick?.input.addEventListener("change", () => {
        chainFor = pick.input.value;
        draw(p);
      });
      const steps: Node[] = [];
      chain.steps.forEach((s, i) => {
        if (i)
          steps.push(el("span", { class: "pcarrow", "aria-hidden": "true" }, icon("chevron-down")));
        steps.push(
          el(
            "div",
            { class: `pc${s.state === "used" ? " win" : ""}` },
            el("span", { class: "pcn" }, String(s.n)),
            el("div", {}, el("b", {}, s.title), el("div", { class: "small muted" }, s.line)),
            el(
              "div",
              { class: "pcv" },
              el("b", {}, s.rate ?? "–"),
              s.state === "used"
                ? chip("Used", "ok")
                : s.state === "skipped"
                  ? chip("Skipped")
                  : chip("Not set"),
            ),
          ),
        );
      });
      parts.push(
        card(
          "Pay rate card",
          "The first one that's set is paid.",
          pick
            ? [pick.el]
            : [chip(`${chain.client}${chain.service ? ` · ${chain.service}` : ""}`, "info")],
          el("div", { class: "paychain" }, ...steps),
        ),
      );
    }
    host.replaceChildren(...parts);
  }

  function addModal(p: Pkg) {
    const whose = select(
      "Client",
      [["", "Default (any client)"], ...p.clients.map((c) => [c.id, c.name] as const)],
      "",
    );
    const service = select(
      "Service",
      [["", "Any service"], ...p.services.map((s) => [s.id, s.name] as const)],
      "",
    );
    const rate = field("Pay rate ($)", "", { inputmode: "decimal", placeholder: "19.00" });
    const per = select(
      "Per",
      [
        ["hour", "Per hour"],
        ["visit", "Per visit"],
      ],
      "hour",
    );
    const from = field("Starts", p.today, { type: "date" });
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const m = openModal({
      title: `Agreed pay rate · ${p.person.name}`,
      sub: "From the signed agreement. It starts on the day picked and ends the one before.",
      body: [
        el(
          "div",
          { class: "form formgrid", style: "max-width:none" },
          whose.el,
          service.el,
          rate.el,
          per.el,
          from.el,
        ),
      ],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await api("POST", "/api/rates/pay", {
        level: whose.input.value ? "person_client" : "person",
        personId: p.person.id,
        clientId: whose.input.value || null,
        serviceId: service.input.value || null,
        rate: rate.input.value,
        per: per.input.value,
        from: from.input.value,
      });
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast("Agreed pay rate saved");
      await load();
    });
  }

  await load();
}
